<template>
  <a
    v-if="href"
    :href="href"
    :class="componentClasses"
    :disabled="disabled"
  >
    <div
      v-if="showIconBefore || iconBefore !== ''"
      class="stk-menu-icon-before"
    >
      <Icon
        v-if="iconBefore"
        :icon="iconBefore"
      />
    </div>
    <Badge
      v-if="index"
      :status="badgeStatus"
      resizable
    >{{ index }}</Badge>
    <Icon
      v-if="icon"
      :icon="icon"
    />
    <span><slot /></span>
    <Badge
      v-if="count"
      :status="badgeStatus"
      resizable
    >{{ count }}</Badge>
    <Icon v-if="selected" class="stk-menu-item-icon-selected" icon="fas fa-check-circle" />
  </a>
  <button
    v-else
    :class="componentClasses"
    :disabled="disabled"
    @click="$emit('click')"
  >
    <div
      v-if="showIconBefore || iconBefore !== ''"
      class="stk-menu-icon-before"
    >
      <Icon
        v-if="iconBefore"
        :icon="iconBefore"
      />
    </div>
    <Badge
      v-if="index"
      :status="badgeStatus"
      resizable
    >
      {{ index }}
    </Badge>
    <Icon
      v-if="icon"
      :icon="icon"
    />
    <span><slot /></span>
    <Badge
      v-if="count"
      :status="badgeStatus"
      resizable
    >
      {{ count }}
    </Badge>
    <Icon v-if="selected" class="stk-menu-item-icon-selected" icon="fas fa-check-circle" />
  </button>
</template>

<script setup>
import { computed } from 'vue';
import Badge from '@stakeui/content/badge/Badge.vue';
import Icon from '@stakeui/graphic/icon/Icon.vue';

const props = defineProps({
  href: {
    type: String,
  },
  active: {
    type: Boolean,
  },
  icon: {
    type: String,
  },
  count: {
    type: [Number, String],
  },
  index: {
    type: [Number, String],
  },
  variant: {
    type: String,
  },
  selected: {
    type: Boolean,
    default: false,
  },
  static: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  iconBefore: {
    type: String,
    default: '',
  },
  showIconBefore: {
    type: Boolean,
    default: false,
  },
});

const badgeStatus = computed(() => props.variant ?? (props.active ? 'primary' : ''));

const componentClasses = computed(() => [
  'stk-menu-item',
  props.active ? 'is-active' : '',
  props.variant ? `variant-${props.variant}` : '',
  props.selected && `is-selected`,
  props.static && `is-static`,
]);
</script>

<style>
.stk-menu-item {
  --icon-color: var(--stk-primary);
  --icon-before-color: var(--stk-text-weak);
  --hover-bg: var(--stk-primary-95);
  --hover-fg: var(--stk-primary-30);
  display: flex;
  align-items: center;
  text-align: start;
  cursor: pointer;
  justify-content: start;
  line-height: 1.25;
  padding: 0.375em 0.625em;
  border-radius: 0.625em;
  font-weight: 500;
  color: var(--stk-text-normal);
  gap: 0.5em;
  transition-duration: var(--stk-transition-duration);
  transition-property: background-color, color;

  &:hover {
    --icon-color: var(--stk-primary-50);
    background-color: var(--hover-bg);
    color: var(--hover-fg);
  }

  &[disabled] {
    box-shadow: none;
    opacity: 0.5;
    pointer-events: none;
  }

  &.is-active {
    --icon-color: var(--stk-primary-50);
    --icon-before-color: var(--stk-primary-50);
    background-color: var(--stk-primary-90);
    color: var(--stk-primary-30);
  }

  &.is-selected {
    --icon-color: var(--stk-success-40);
    background-color: var(--stk-success-90);
    color: var(--stk-success-30);
  }

  &.is-static {
    cursor: default;
    pointer-events: none;
  }

  &.variant-danger {
    --icon-color: var(--stk-danger-60);
    --hover-bg: var(--stk-danger-95);
    --hover-fg: var(--stk-danger-30);

    &.is-active {
      background-color: var(--stk-danger-95);
      color: var(--stk-danger-30);
    }
  }

  &.variant-warning {
    --icon-color: var(--stk-warning-60);
    --hover-bg: var(--stk-warning-95);
    --hover-fg: var(--stk-warning-30);

    &.is-active {
      background-color: var(--stk-warning-95);
      color: var(--stk-warning-30);
    }
  }

  &.variant-info {
    --icon-color: var(--stk-info-60);
    --hover-bg: var(--stk-info-95);
    --hover-fg: var(--stk-info-30);

    &.is-active {
      background-color: var(--stk-info-95);
      color: var(--stk-info-30);
    }
  }

  &.variant-success {
    --icon-color: var(--stk-success-60);
    --hover-bg: var(--stk-success-95);
    --hover-fg: var(--stk-success-30);

    &.is-active {
      background-color: var(--stk-success-95);
      color: var(--stk-success-30);
    }
  }

  .stk-icon {
    color: var(--icon-color);
  }

  .stk-badge:first-child:not(:only-child) {
    margin: -0.125em 0.125em -0.125em -0.25em;
  }

  .stk-badge:last-child:not(:only-child) {
    margin: -0.125em -0.25em -0.125em auto;
  }
}

.stk-menu-item-icon-selected {
  margin: -0.125em;
  margin-left: auto;
}

.stk-menu-icon-before {
  display: flex;
  height: 1.25em;
  width: 1.25em;
  flex-shrink: 0;
  margin-right: 0.25em;

  .stk-icon {
    color: var(--icon-before-color);
  }
}
</style>
